import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Loader2 } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PdfPages = ({ blob }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const container = ref.current;
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ data: await blob.arrayBuffer() }).promise;
        for (let n = 1; n <= pdf.numPages && !cancelled; n += 1) {
          const page = await pdf.getPage(n);
          const width = container.clientWidth || 800;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (width / base.width) * 2 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          canvas.className = 'w-full h-auto bg-white shadow mb-3';
          canvas.setAttribute('data-testid', `bolletta-viewer-page-${n}`);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (!cancelled) container.appendChild(canvas);
        }
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (container) container.innerHTML = ''; };
  }, [blob]);
  if (failed) return <p className="text-sm text-red-600 p-6">Anteprima non disponibile: usa "Scarica".</p>;
  return <div ref={ref} className="w-full p-2" />;
};

export const BollettaViewerDialog = ({ leadId, bolletta, onClose }) => {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bolletta) return undefined;
    let objectUrl;
    setBlob(null); setUrl(null); setError(false);
    axios.get(`${API_URL}/api/leads/${leadId}/bollette-portale/${bolletta.id}/download`, { responseType: 'blob' })
      .then((res) => { objectUrl = URL.createObjectURL(res.data); setBlob(res.data); setUrl(objectUrl); })
      .catch(() => setError(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [leadId, bolletta]);

  const ext = (bolletta?.file_path || '').split('.').pop()?.toLowerCase() || 'pdf';
  const nomeFile = `Bolletta_${(bolletta?.fornitura || '').toUpperCase()}_${bolletta?.fornitore || 'cliente'}.${ext}`.replace(/[^\w.-]+/g, '_');
  const isImage = blob?.type?.startsWith('image/');

  const scarica = () => {
    const a = document.createElement('a');
    a.href = url; a.download = nomeFile;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={!!bolletta} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[95vw] max-w-4xl p-4 sm:p-6" data-testid="bolletta-viewer-dialog">
        <DialogHeader className="text-left pr-8">
          <DialogTitle className="truncate">Bolletta {bolletta?.fornitura?.toUpperCase()} · {bolletta?.fornitore || 'Fornitore N/D'}</DialogTitle>
          <DialogDescription>Documento caricato dal cliente nell'Area Clienti.</DialogDescription>
        </DialogHeader>
        <Button size="sm" onClick={scarica} disabled={!url} className="w-full sm:w-auto sm:justify-self-start" data-testid="bolletta-viewer-download">
          <Download className="w-4 h-4 mr-1.5" /> Scarica bolletta
        </Button>
        <div className="h-[65vh] rounded-lg border border-slate-200 bg-slate-100 overflow-auto" data-testid="bolletta-viewer-content">
          {error && <p className="text-sm text-red-600 p-6">Impossibile caricare la bolletta.</p>}
          {!error && !url && <div className="h-full grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>}
          {url && isImage && <img src={url} alt="Bolletta cliente" className="w-full h-auto" />}
          {url && blob && !isImage && <PdfPages blob={blob} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
